import React, { Component, useState } from 'react';
import { withRouter } from 'react-router';
import axios from 'axios';
import {
  Form as InformedForm,
  Text as InformedText,
  TextArea as InformedTextArea,
} from 'informed';
import styled from 'styled-components';
import { environment } from '../../../utils';
import i18next from '../../../i18n';
import { InfoIcon } from '../../common/Icons';

const t = i18next.t;

const BACKEND_URL = environment.REACT_APP_BACKEND_HTTP_URL;

const Form = styled(InformedForm)`
  margin: 3rem 0;

  input,
  text,
  textarea {
    font-family: inherit;
    font-size: 1rem;
    border: 2px solid ${(p) => p.theme.color.boldText};
  }

  p {
    max-width: 70ch;
  }

  fieldset {
    border: none;
    border-top: 0.2rem solid ${(p) => p.theme.color.boldText};
    margin-top: 2rem;
  }

  legend {
    font-size: 1.2rem;
    padding: 0 0.5em;
  }

  strong {
    color: ${(p) => p.theme.color.boldText};
  }
`;

const Fieldset = styled.fieldset`
  @media screen and (min-width: 800px) {
    label {
      display: flex;
      justify-content: space-between;
    }
  }
`;

const Text = styled(InformedText)`
  padding: 0.5rem;
  max-width: 500px;
  width: 100%;
`;

const TextArea = styled(InformedTextArea)`
  padding: 0.5rem;
  max-width: 500px;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  margin: 1em 0;

  input:not([type='checkbox']):not([type='radio']),
  textarea {
    display: block;
  }

  input[type='radio'],
  input[type='checkbox'] {
    margin: 0 1rem;
  }
`;

const Submit = styled.button`
  display: block;
  width: 7em;
  padding: 1em 1.5em;
  margin: 2em auto;
  background-color: ${(p) => p.theme.color.boldText};
  font-size: 1.1rem;
  color: ${(p) => p.theme.color.white100};

  border: none;
  outline: none;
  cursor: pointer;

  &:hover {
    background-color: ${(p) => p.theme.color.redMedium};
  }
`;

const ExpanderButton = styled.button`
  color: ${(p) => p.theme.color.boldText};
  border: none;
  background-color: transparent;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  height: 1.5em;

  &:hover {
    color: ${(p) => p.theme.color.redMedium};
  }
`;

const ExpanderText = styled.p`
  color: ${(p) => p.theme.color.linkColor};
  margin-top: 0;
  margin-bottom: 1em;
`;

const Info = styled.p`
  margin-left: 2em;
  color: ${(p) => p.theme.color.boldText};
`;

const Error = styled.div`
  color: ${(p) => p.theme.color.boldText};
`;

const RoleExpander = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 500px;
  width: 100%;
`;

const RoleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;
  width: 100%;
`;

const RoleInputContainer = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  flex-grow: 1;
`;

const RoleLabel = styled.label`
  margin-left: 0.5rem;
`;

const RoleSelectionInfo = styled.p`
  font-size: 0.8rem;
`;

const RoleSelectionContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: right;
  flex-direction: column;
`;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxWithToggle = ({
  roleKey,
  onCheckboxChange,
  expandedRole,
  setExpandedRole,
}) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
    onCheckboxChange(roleKey, !isChecked);
  };

  const handleToggleChange = (event) => {
    event.preventDefault();
    setExpandedRole(roleKey !== expandedRole ? roleKey : null);
  };

  const isExpanded = roleKey === expandedRole;

  return (
    <RoleExpander>
      <RoleContainer>
        <RoleInputContainer onClick={handleCheckboxChange}>
          <input
            value={roleKey}
            name={roleKey}
            type="checkbox"
            checked={isChecked}
            onChange={handleCheckboxChange}
          />
          <RoleLabel>{t(`rekry.role.${roleKey}.title`)}</RoleLabel>
        </RoleInputContainer>
        <ExpanderButton onClick={handleToggleChange}>
          <InfoIcon />
        </ExpanderButton>
      </RoleContainer>
      {isExpanded && (
        <ExpanderText>{t(`rekry.role.${roleKey}.description`)}</ExpanderText>
      )}
    </RoleExpander>
  );
};

const roleKeys = [
  'editor',
  'promo',
  'corporate',
  'communications',
  'graphics',
  'construction',
  'studio',
  'IT',
];

class RecruitmentForm extends Component {
  state = {
    selectedRoles: [],
    expandedRole: null,
    errors: {},
  };

  setExpandedRole = (roleKey) => {
    this.setState({ expandedRole: roleKey });
  };

  setFormApi = (formApi) => {
    this.formApi = formApi;
  };

  validateForm = () => {
    const errors = {};
    const { email, name, message } = this.formApi.getState().values;
    const { selectedRoles } = this.state;

    if (!email) errors.email = t('rekry.empty');
    if (!name) errors.name = t('rekry.empty');
    if (!message) errors.message = t('rekry.empty');
    if (selectedRoles.length === 0) errors.roles = t('rekry.selectRole');

    return errors;
  };

  handleSubmit = async () => {
    const { history } = this.props;
    const formState = this.formApi.getState();

    const errors = this.validateForm();
    this.setState({ errors });

    if (Object.keys(errors).length) return;

    if (this.state.selectedRoles) {
      const roles = roleKeys.filter((role) =>
        this.state.selectedRoles.includes(role)
      );
      formState.values.roles = roles.map((role) =>
        t(`rekry.role.${role}.title`)
      );
    }

    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/recruitment`,
        formState.values,
        {
          headers: {
            'Accept-Language': i18next.language,
          },
        }
      );
      console.log(resp);
      const email = formState.values.email;
      history.push(`/rekry/success?email=${email}`);
    } catch (err) {
      console.error(err);
      history.push('/rekry/error');
    }
  };

  getErrors = () => this.state.errors;

  getValues = () => (this.formApi ? this.formApi.getState().values || {} : {});

  renderErrors = () => {
    const errors = this.getErrors();
    const hasErrors = !!Object.keys(errors).length;
    if (!hasErrors) return null;

    return <Error>{t('rekry.formError')}</Error>;
  };

  rerender = () => {
    // force rerender
    this.setState({});
  };

  handleSubmitFailure = (data) => {
    this.rerender();
    return true;
  };

  handleCheckboxChange = (role, isChecked) => {
    this.setState((prevState) => {
      const alreadySelected = prevState.selectedRoles.includes(role);

      if (isChecked && !alreadySelected) {
        return { selectedRoles: [...prevState.selectedRoles, role] };
      } else if (!isChecked && alreadySelected) {
        return {
          selectedRoles: prevState.selectedRoles.filter((r) => r !== role),
        };
      } else {
        return prevState;
      }
    });
  };

  render() {
    const errors = this.getErrors();

    return (
      <Form
        getApi={this.setFormApi}
        onSubmit={this.handleSubmit}
        onSubmitFailure={this.handleSubmitFailure}
      >
        <Fieldset>
          <Label>
            <LabelContainer>
              {t('rekry.name')}
              {errors.name && <Error>{errors.name}</Error>}
            </LabelContainer>
            <Text field="name" placeholder={t('rekry.namePlaceholder')} />
          </Label>
          <Label>
            <LabelContainer>
              {t('rekry.email')}
              {errors.email && <Error>{errors.email}</Error>}
            </LabelContainer>
            <Text field="email" type="email" placeholder="email@example.com" />
          </Label>
          <div>
            {t('rekry.roles')}
            <RoleSelectionInfo>{t('rekry.rolesInfo')}</RoleSelectionInfo>
            {errors.roles && <Error>{errors.roles}</Error>}
            <RoleSelectionContainer>
              {roleKeys.map((roleKey) => (
                <CheckboxWithToggle
                  key={roleKey}
                  roleKey={roleKey}
                  onCheckboxChange={this.handleCheckboxChange}
                  expandedRole={this.state.expandedRole}
                  setExpandedRole={this.setExpandedRole}
                />
              ))}
            </RoleSelectionContainer>
          </div>
          <Label>
            <LabelContainer>
              {t('rekry.application')}
              {errors.message && <Error>{errors.message}</Error>}
            </LabelContainer>
            <TextArea
              field="message"
              placeholder={t('rekry.applicationPlaceholder')}
              rows="4"
            />
          </Label>
        </Fieldset>
        <fieldset>
          {this.renderErrors()}
          <br />
          <Info style={{ textAlign: 'center', margin: 'auto' }}>
            {t('rekry.concent')}
          </Info>
          <Submit type="submit">{t('rekry.send')}</Submit>
        </fieldset>
      </Form>
    );
  }
}

export default withRouter(RecruitmentForm);
